import { useLayoutEffect, useRef, useState } from "react";

/**
 * 고정 설계 폭으로 그린 화면을 담을 자리 폭에 맞춰 축소한다.
 *
 * 실제 화면을 좁은 컨테이너 안에서 다시 레이아웃하면(폭을 줄이면) 그리드가 접히고 줄바꿈이
 * 달라져 `같은 화면`이 아니게 된다. 그래서 안쪽은 설계 폭 그대로 그리고 transform 으로만 줄인다.
 *
 * transform 은 요소가 차지하는 레이아웃 높이를 줄여 주지 않아, 축소된 높이를 직접 계산해
 * 겉 박스에 물려야 아래쪽에 빈 공간이 남지 않는다.
 */
function useScaleToFit(designWidth: number) {
  // 가용 폭을 재는 겉 박스 — 축소된 높이도 이쪽에 걸어야 한다.
  const frameRef = useRef<HTMLDivElement>(null);
  // 설계 폭으로 그려지는 안쪽 — 원본 높이를 여기서 읽는다.
  const contentRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [contentHeight, setContentHeight] = useState(0);

  useLayoutEffect(() => {
    const frame = frameRef.current;
    const content = contentRef.current;
    if (!frame || !content) return;

    // 자리가 설계 폭보다 넓어도 확대하지는 않는다 — 실제 화면보다 커지면 안 된다.
    const measureScale = () =>
      setScale(Math.min(1, frame.clientWidth / designWidth));
    // 원본 높이라서 scale 을 곱하기 전 값이다.
    const measureHeight = () => setContentHeight(content.offsetHeight);

    measureScale();
    measureHeight();

    const frameObserver = new ResizeObserver(measureScale);
    frameObserver.observe(frame);
    // 목 데이터라도 이미지 로드나 폰트 적용으로 높이가 뒤늦게 바뀐다.
    const contentObserver = new ResizeObserver(measureHeight);
    contentObserver.observe(content);

    return () => {
      frameObserver.disconnect();
      contentObserver.disconnect();
    };
  }, [designWidth]);

  return { frameRef, contentRef, scale, scaledHeight: contentHeight * scale };
}

export default useScaleToFit;
